import { supabase } from '../lib/supabase';
import emailjs from '@emailjs/browser';

/**
 * Dealer Onboarding Service
 * Handles the full dealer invitation and account creation flow:
 * 1. Master admin creates dealer record + sends invite email
 * 2. Dealer clicks invite link -> sets password -> creates Supabase auth account
 * 3. Dealer connects their Stripe account
 * 4. Dealer can now log in and manage inventory
 */

const EMAILJS_SERVICE_ID = import.meta.env.VITE_EMAILJS_SERVICE_ID || '';
const EMAILJS_TEMPLATE_ID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || '';
const EMAILJS_PUBLIC_KEY = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || '';

class DealerOnboardingService {
  // ==========================================
  // INVITATION MANAGEMENT
  // ==========================================

  generateInviteToken() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let token = '';
    for (let i = 0; i < 48; i++) {
      token += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return token;
  }

  async inviteDealer(dealerId, email, invitedBy = null) {
    try {
      const token = this.generateInviteToken();
      const inviteUrl = `${window.location.origin}/dealer/onboard?token=${token}`;

      const { data, error } = await supabase
        .from('dealer_invitations')
        .insert([{
          dealer_id: dealerId,
          email: email,
          invite_token: token,
          status: 'pending',
          invited_by: invitedBy,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        }])
        .select()
        .single();

      if (error) throw error;

      // Update dealer onboarding status
      await supabase
        .from('dealers')
        .update({ onboarding_status: 'invited' })
        .eq('id', dealerId);

      // Send invite email
      await this.sendInviteEmail(email, inviteUrl, dealerId);

      return { data: { ...data, invite_url: inviteUrl }, error: null };
    } catch (err) {
      console.error('Invite Dealer Error:', err);
      return { data: null, error: err };
    }
  }

  async sendInviteEmail(email, inviteUrl, dealerId) {
    try {
      // Fetch dealer name for the email
      const { data: dealer } = await supabase
        .from('dealers')
        .select('company_name')
        .eq('id', dealerId)
        .single();

      const companyName = dealer?.company_name || 'Your Company';

      const templateParams = {
        to_email: email,
        to_name: companyName,
        from_name: 'ShedX Platform',
        subject: `You're invited to sell on ShedX!`,
        message: `Welcome to ShedX, ${companyName}!\n\nYou've been invited to join ShedX as a licensed dealer. Click the link below to set up your account, connect your Stripe payment processing, and start listing your shed inventory.\n\nSet up your account: ${inviteUrl}\n\nThis invite link expires in 7 days.\n\nOnce you're set up, you'll be able to:\n- Upload and manage your shed inventory with photos\n- Accept payments directly to your Stripe account\n- Track leads and customer inquiries\n- Run sales and promotions\n\nQuestions? Reply to this email or contact our support team.`,
        reply_to: 'info@seyardsolutions.com'
      };

      if (EMAILJS_SERVICE_ID && EMAILJS_PUBLIC_KEY) {
        await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
        console.log('Invite email sent to:', email);
      } else {
        console.warn('EmailJS not configured. Invite URL:', inviteUrl);
        console.log('Copy this URL and send to dealer manually:', inviteUrl);
      }

      return { error: null };
    } catch (err) {
      console.error('Send Invite Email Error:', err);
      return { error: err };
    }
  }

  async resendInvite(invitationId) {
    try {
      const newToken = this.generateInviteToken();
      const inviteUrl = `${window.location.origin}/dealer/onboard?token=${newToken}`;

      const { data, error } = await supabase
        .from('dealer_invitations')
        .update({
          invite_token: newToken,
          status: 'pending',
          invited_at: new Date().toISOString(),
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        })
        .eq('id', invitationId)
        .select('*, dealers(company_name)')
        .single();

      if (error) throw error;

      await this.sendInviteEmail(data.email, inviteUrl, data.dealer_id);

      return { data: { ...data, invite_url: inviteUrl }, error: null };
    } catch (err) {
      console.error('Resend Invite Error:', err);
      return { data: null, error: err };
    }
  }

  // ==========================================
  // INVITATION VALIDATION & ACCEPTANCE
  // ==========================================

  async validateInvite(token) {
    try {
      const { data, error } = await supabase
        .from('dealer_invitations')
        .select('*, dealers(id, company_name, email, phone, city, state, subscription_plan, lot_locations)')
        .eq('invite_token', token)
        .single();

      if (error) throw error;
      if (!data) return { valid: false, error: 'Invitation not found' };
      if (data.status === 'accepted') return { valid: false, error: 'Invitation already used', data };
      if (new Date(data.expires_at) < new Date()) return { valid: false, error: 'Invitation expired', data };

      return { valid: true, data, error: null };
    } catch (err) {
      console.error('Validate Invite Error:', err);
      return { valid: false, error: err.message || 'Failed to validate invitation' };
    }
  }

  async acceptInvite(token, password) {
    try {
      // Validate the token first
      const validation = await this.validateInvite(token);
      if (!validation.valid) {
        return { data: null, error: validation.error };
      }

      const invitation = validation.data;

      // Create Supabase auth user for the dealer
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: invitation.email,
        password: password,
        options: {
          emailRedirectTo: `${window.location.origin}/dealer/login`,
          data: {
            role: 'dealer_admin',
            dealer_id: invitation.dealer_id,
            company_name: invitation.dealers?.company_name,
            shedx_role: 'dealer_admin'
          }
        }
      });

      if (authError) throw authError;

      // Sign in immediately so the user has a valid session
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: invitation.email,
        password: password
      });

      // Use whichever user object is available
      const activeUser = signInData?.user || authData.user;
      const userId = activeUser?.id;

      if (signInError) {
        console.warn('Auto sign-in after signup failed (email confirmation may be required):', signInError.message);
      }

      if (userId) {
        // Create user_profiles entry
        const { error: profileErr } = await supabase
          .from('user_profiles')
          .upsert([{
            id: userId,
            email: invitation.email,
            role: 'dealer_admin',
            dealer_id: invitation.dealer_id
          }]);
        if (profileErr) {
          console.warn('Could not create user profile during onboarding:', profileErr.message);
        }
      }

      // Mark invitation as accepted
      await supabase
        .from('dealer_invitations')
        .update({
          status: 'accepted',
          accepted_at: new Date().toISOString()
        })
        .eq('invite_token', token);

      // Update dealer onboarding status
      await supabase
        .from('dealers')
        .update({
          onboarding_status: 'account_created',
          onboarding_completed_at: new Date().toISOString()
        })
        .eq('id', invitation.dealer_id);

      return { data: { user: authData.user, dealer: invitation.dealers }, error: null };
    } catch (err) {
      console.error('Accept Invite Error:', err);
      return { data: null, error: err.message || 'Failed to create account' };
    }
  }

  // ==========================================
  // INVITATION QUERIES
  // ==========================================

  async getInvitationsForDealer(dealerId) {
    try {
      const { data, error } = await supabase
        .from('dealer_invitations')
        .select('*')
        .eq('dealer_id', dealerId)
        .order('invited_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Get Invitations Error:', err);
      return { data: [], error: err };
    }
  }

  async getAllInvitations() {
    try {
      const { data, error } = await supabase
        .from('dealer_invitations')
        .select('*, dealers(company_name, email)')
        .order('invited_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Get All Invitations Error:', err);
      return { data: [], error: err };
    }
  }

  async cancelInvitation(invitationId) {
    try {
      const { error } = await supabase
        .from('dealer_invitations')
        .update({ status: 'cancelled' })
        .eq('id', invitationId);

      if (error) throw error;
      return { error: null };
    } catch (err) {
      console.error('Cancel Invitation Error:', err);
      return { error: err };
    }
  }
}

export const dealerOnboardingService = new DealerOnboardingService();
export default dealerOnboardingService;
